import { useRef, useState } from 'react';
import {
	Button,
	ButtonGroup,
	ClickAwayListener,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper,
} from '@mui/material';
import { KeyValueEntity } from '../../Types/base';
import './biz-split-button.scss';
import { getIconClassByType, TBizIcon } from './BizIcon';
import { TBizButtonSize } from './BizButton';

type BizSplitButtonProps = {
	options: KeyValueEntity[];
	handleClick: (key: string) => void;
	isLead?: boolean;
	size?: TBizButtonSize;
	noShadow?: boolean;
	icon?: TBizIcon | 'CUSTOM';
	customIcon?: string;
};

export const BizSplitButton = ({ options, handleClick, isLead, size = 'MEDIUM', noShadow, icon, customIcon }: BizSplitButtonProps) => {
	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);
	const [selectedKey, setSelectedKey] = useState('0');

	const handleMenuItemClick = (_event: React.MouseEvent<HTMLLIElement, MouseEvent>, key: string) => {
		setSelectedKey(key);
		handleClick(key);
		setOpen(false);
	};

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const getIconClass = () => {
		if (icon === 'CUSTOM') {
			return customIcon ?? '';
		} else if (icon) {
			return getIconClassByType(icon);
		}

		return 'fas fa-caret-down';
	};

	return (
		<div className={`biz-split-button${isLead ? ' biz-split-button--lead' : ''}${size ? ` biz-split-button--size-${size.toLowerCase()}` : ''}${noShadow ? ' biz-split-button--no-shadow' : ''}`}>
			<ButtonGroup variant='contained' ref={anchorRef} aria-label='Button group with a nested menu'>
				<Button size={size === 'SMALL' ? 'small' : 'medium'} onClick={() => handleClick(options[0].Key)}>
					{options[0].Value}
				</Button>
				<Button
					size={size === 'SMALL' ? 'small' : 'medium'}
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label='select'
					aria-haspopup='menu'
					onClick={handleToggle}
				>
					<i className={getIconClass()}></i>
				</Button>
			</ButtonGroup>
			<Popper
				sx={{
					zIndex: 1,
				}}
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id='split-button-menu' autoFocusItem>
									{options
										.filter((o) => o.Key !== options[0].Key)
										.map((option) => (
											<MenuItem
												key={option.Key}
												selected={option.Key === selectedKey}
												onClick={(event) => handleMenuItemClick(event, option.Key)}
											>
												{option.Value}
											</MenuItem>
										))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</div>
	);
};

export default BizSplitButton;
